import type {ReactElement, ReactNode} from 'react';
import styles from './ai-chat-memory-view.module.scss';
import {MEMORY_SKELETON_COUNT} from './ai-chat-memory-view.types';
import {AiChatMemoryAccountSection} from '../ai-chat-memory-account-section/ai-chat-memory-account-section';
import {AiChatMemoryCard} from './components/ai-chat-memory-card/ai-chat-memory-card';
import {MemoryLoadState} from '@AiChat/ai-chat.types';
import {formatMemoryDate} from '@AiChat/lib/ai-chat-memory-library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

export function AiChatMemoryView(): ReactElement {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();
  const memories = state.userMemories;

  const getHeader = (): ReactElement => {
    return (
      <div className={`gap-1 ${styles.header}`}>
        <div className="_position-relative _full-height">
          <Icon
            icon="icon-arrow-left"
            size={isSmall ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24}
            shape={IconShape.SQUARE}
            type={IconType.GHOST}
            isUsedAsButton
            ariaLabel={window.i18next.t('pmwjs_back')}
            onClick={actions.navigateBack}
          />
        </div>
        <div className={`spacing-p-t-1 ${styles.titleWrapper}`}>
          <h2 className={styles.title}>{window.i18next.t('pmwjs_ai_memory_page_title')}</h2>
          <Icon icon="icon-book" size={IconSize.SIZE_ICON_20} shape={IconShape.NONE} className="content-ai spacing-m-l-2" />
        </div>
      </div>
    );
  };

  const getAccountSection = (): ReactNode => {
    if (!memories.account) {
      return null;
    }

    return <AiChatMemoryAccountSection account={memories.account} />;
  };

  const getFactsSection = (): ReactNode => {
    if (memories.facts.length === 0) {
      return null;
    }

    return (
      <div className={styles.section}>
        <Text size={TextSize.LG} bold val={window.i18next.t('pmwjs_ai_memory_facts_heading')} />
        <div className={styles.grid}>
          {memories.facts.map((fact) => (
            <AiChatMemoryCard
              key={fact.id}
              memoryId={fact.id}
              heading={formatMemoryDate(fact.updateTime)}
              body={fact.fact}
              deleteConfirmationText={window.i18next.t('pmwjs_ai_memory_delete_fact_confirmation', {fact: fact.fact, interpolation: {escapeValue: true}})}
              onDelete={(): void => {
                actions.forgetMemoryFact(fact);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const getVisualEntitiesSection = (): ReactNode => {
    if (memories.visualEntities.length === 0) {
      return null;
    }

    return (
      <div className={styles.section}>
        <Text size={TextSize.LG} bold val={window.i18next.t('pmwjs_ai_memory_visuals_heading')} />
        <div className={styles.grid}>
          {memories.visualEntities.map((entity) => (
            <AiChatMemoryCard
              key={entity.id}
              memoryId={entity.id}
              heading={entity.name}
              body={entity.description}
              imageUrl={entity.imageUrl}
              deleteConfirmationText={window.i18next.t('pmwjs_ai_memory_delete_visual_confirmation', {name: entity.name, interpolation: {escapeValue: true}})}
              onDelete={(): void => {
                actions.forgetMemoryVisualEntity(entity);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const getEmptyState = (): ReactNode => {
    if (memories.facts.length > 0 || memories.visualEntities.length > 0) {
      return null;
    }

    return <Text className={styles.placeholder} size={TextSize.MD} val={window.i18next.t('pmwjs_ai_memory_empty')} />;
  };

  const getSkeletons = (): ReactElement => {
    return (
      <div className={styles.section}>
        <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonHeading} />
        <div className={styles.grid}>
          {Array.from({length: MEMORY_SKELETON_COUNT}, (_, index) => (
            <div key={`memory-skeleton-${index}`} className={`spacing-p-4 ${styles.skeletonCard}`}>
              <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonDate} />
              <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonLine} />
              <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonLineShort} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getContent = (): ReactNode => {
    if (state.userMemoriesLoadState === MemoryLoadState.LOADING) {
      return getSkeletons();
    }

    if (state.userMemoriesLoadState === MemoryLoadState.FAILED) {
      return <Text className={styles.placeholder} size={TextSize.MD} val={window.i18next.t('pmwjs_ai_memory_load_failed')} />;
    }

    return (
      <>
        {getAccountSection()}
        {getEmptyState()}
        {getFactsSection()}
        {getVisualEntitiesSection()}
      </>
    );
  };

  const getContainerPaddingClasses = (): string => {
    if (isSmall) {
      return 'spacing-p-t-4 spacing-p-b-4 spacing-p-l-5 spacing-p-r-5';
    }

    return 'spacing-p-t-8 spacing-p-b-8 spacing-p-l-4 spacing-p-r-4';
  };

  return (
    <div className={`flex-v-row _full-height _full-width ${styles.container} ${getContainerPaddingClasses()}`}>
      <div className={`flex-v-row ${isSmall ? 'gap-4' : 'gap-8'} ${styles.content}`}>
        {getHeader()}
        <Text className={styles.subtitle} size={TextSize.MD} val={window.i18next.t('pmwjs_ai_memory_page_subtitle')} />
        {getContent()}
      </div>
    </div>
  );
}
